import Stack from '@mui/material/Stack';
import Button from 'components/Button/Button';
import TagSelect from 'components/EditableTags/TagSelect';
import IconButton from 'components/IconButton/IconButton';
import Tag from 'components/Tag/Tag';
import Tooltip from 'components/Tooltip';
import { CirclePlusIcon, PencilIcon } from 'components/icons';
import useAlert from 'hooks/useAlert';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createTag, deleteTagByName, getTags } from 'services/tags.service';
import toast from 'utils/toast';

const EditableTags = ({
  row,
  onTagsUpdate,
  objectType = 'group', // 'group' or 'workflow'
  getObjectTags, // function to get available tags for this object type
  getObjectId = (row) => row.group_id, // function to extract object ID from row
  getObjectHash = (row) => row.hash, // function to extract object hash from row
}) => {
  const alert = useAlert();
  const account = useSelector((s) => s.settings.account);

  const handleAddTags = async (e) => {
    e.stopPropagation();
    const currentTagNames = (row.tags || []).map((t) =>
      typeof t === 'string' ? t : t.tag,
    );

    alert.prompt({
      title: 'Add Tags',
      inputValue: currentTagNames,
      confirmationText: 'Confirm',
      renderInput: (value, onChange) => (
        <TagSelect
          value={value || []}
          label="Tags (Optional)"
          onChange={onChange}
          placeholder="Select or create tags..."
        />
      ),
      onConfirm: async (newTagNames) => {
        try {
          const tagsToRemove = currentTagNames.filter(
            (tag) => !newTagNames.includes(tag),
          );
          const tagsToAdd = newTagNames.filter(
            (tag) => !currentTagNames.includes(tag),
          );

          const objectId = getObjectId(row);

          if (tagsToRemove.length > 0) {
            await Promise.all(
              tagsToRemove.map((tag) =>
                deleteTagByName(objectType, objectId, tag, account.account_id),
              ),
            );
          }

          if (tagsToAdd.length > 0) {
            await createTag({
              object_name: objectType,
              object_id: objectId,
              account_id: account.account_id,
              tag: tagsToAdd,
            });
          }

          const updatedTags = newTagNames.map((tagName) => ({
            tag: tagName,
          }));
          if (onTagsUpdate) {
            onTagsUpdate(getObjectHash(row), updatedTags);
          }

          toast.success('Tags updated successfully');
        } catch (error) {
          toast.error('Failed to update tags. Please try again.');
        }
      },
    });
  };

  const tagLabels = (row.tags || []).map((t) =>
    typeof t === 'string' ? t : t.tag,
  );
  const maxDisplayTags = 3;
  const hasHiddenTags = tagLabels.length > maxDisplayTags;
  const displayedTags = hasHiddenTags
    ? tagLabels.slice(0, maxDisplayTags - 1)
    : tagLabels;
  const hiddenCount = hasHiddenTags
    ? tagLabels.length - (maxDisplayTags - 1)
    : 0;

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="flex-start"
    >
      {displayedTags.map((tagLabel, index) => (
        <Tag
          key={tagLabel || index}
          label={tagLabel}
          variant="outlined"
          color="primary"
        />
      ))}

      {hasHiddenTags && (
        <Tooltip
          // force the line break with the <br />
          title={tagLabels.slice(maxDisplayTags - 1).map((tag) => [
            <>
              {tag} <br />
            </>,
          ])}
        >
          <span>
            <Tag
              label={`+${hiddenCount}`}
              variant="outlined"
              size="small"
              color="primary"
            />
          </span>
        </Tooltip>
      )}

      {tagLabels.length > 0 ? (
        <IconButton onClick={handleAddTags} size="mini" icon={<PencilIcon />} />
      ) : (
        <Button
          color="secondary"
          startIcon={null}
          onClick={handleAddTags}
          size="mini"
        >
          Add
        </Button>
      )}
    </Stack>
  );
};

export default EditableTags;
